import api from './axios';
import { AxiosError } from 'axios';

// Backend response wrapper interface
interface BackendResponse<T> {
  data: T;
  message: string;
  success: boolean;
  timestamp: string;
}

export enum PondType {
  GROWOUT = 'growout',
  NURSERY = 'nursery',
  BREEDING = 'breeding',
  QUARANTINE = 'quarantine',
  OTHER = 'other'
}

export enum WaterSource {
  WELL = 'well',
  RIVER = 'river',
  LAKE = 'lake',
  RAINWATER = 'rainwater',
  MUNICIPAL = 'municipal',
  OTHER = 'other'
}

export interface Pond {
  id: string;
  name: string;
  type: PondType;
  area: number;
  depth: number;
  waterSource: WaterSource;
  capacity?: number;
  farmId: string;
  tenantId: string;
  description?: string;
  feedType?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePondDto {
  name: string;
  type: PondType;
  area: number;
  depth: number;
  waterSource: WaterSource;
  capacity?: number;
  farmId: string;
  description?: string;
  feedType?: string;
}

export interface UpdatePondDto {
  name?: string;
  type?: PondType;
  area?: number;
  depth?: number;
  waterSource?: WaterSource;
  capacity?: number;
  farmId?: string;
  description?: string;
  feedType?: string;
}

// Type guard to check if an object is a Pond
function isPond(obj: any): obj is Pond {
  return obj && typeof obj === 'object' && 'id' in obj && 'name' in obj && 'farmId' in obj;
}

export const fetchPonds = async (): Promise<Pond[]> => {
  try {
    const response = await api.get<BackendResponse<Pond[]>>('/ponds');
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data && Array.isArray(responseData.data)) {
      return responseData.data;
    }
    
    // Fallback: check if the response is directly an array
    if (Array.isArray(responseData)) {
      return responseData;
    }
    
    console.error('Invalid ponds response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error fetching ponds:', error);
    throw error;
  }
};

export const fetchPond = async (id: string): Promise<Pond> => {
  try {
    const response = await api.get<BackendResponse<Pond>>(`/ponds/${id}`);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a Pond object
    if (isPond(responseData)) {
      return responseData;
    }
    
    console.error('Invalid pond response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error(`Error fetching pond ${id}:`, error);
    throw error;
  }
};

export const createPond = async (pond: CreatePondDto): Promise<Pond> => {
  try {
    const response = await api.post<BackendResponse<Pond>>('/ponds', pond);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a Pond object
    if (isPond(responseData)) {
      return responseData;
    }
    
    console.error('Invalid create pond response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error creating pond:', error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const updatePond = async (id: string, pond: UpdatePondDto): Promise<Pond> => {
  try {
    const response = await api.patch<BackendResponse<Pond>>(`/ponds/${id}`, pond);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a Pond object
    if (isPond(responseData)) {
      return responseData;
    }
    
    console.error('Invalid update pond response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error(`Error updating pond ${id}:`, error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const deletePond = async (id: string): Promise<void> => {
  try {
    await api.delete(`/ponds/${id}`);
  } catch (error) {
    console.error(`Error deleting pond ${id}:`, error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export async function getPondSummary(id: string) {
  const response = await api.get(`/ponds/${id}/summary`);
  
  // Handle wrapped response from TransformInterceptor
  const responseData = response.data;
  
  // Check if the response has the wrapped format
  if (responseData && responseData.data) {
    return responseData.data;
  }
  
  // Fallback: return the response directly if not wrapped
  return responseData;
}

export async function clearPondCache(id: string) {
  const response = await api.post(`/ponds/${id}/clear-cache`);
  return response.data;
}

export async function getCycles(pondId: string) {
  const res = await api.get(`/ponds/${pondId}/cycles`);
  return res.data;
}

export async function createCycle(pondId: string, data: any) {
  const res = await api.post(`/ponds/${pondId}/cycles`, data);
  return res.data;
}

export async function updateCycle(cycleId: string, data: any) {
  const res = await api.patch(`/ponds/cycles/${cycleId}`, data);
  return res.data;
}

export async function getFeedRecords(pondId: string) {
  const res = await api.get(`/ponds/${pondId}/feed-records`);
  return res.data;
}

export async function getTodayFeedRecords() {
  const today = new Date().toISOString().split('T')[0];
  const res = await api.get(`/ponds/feed-records/today?date=${today}`);
  
  // Handle wrapped response from TransformInterceptor
  const responseData = res.data;
  
  // Check if the response has the wrapped format
  if (responseData && responseData.data && Array.isArray(responseData.data)) {
    return responseData.data;
  }
  
  // Fallback: check if the response is directly an array
  if (Array.isArray(responseData)) {
    return responseData;
  }
  
  console.error('Invalid today feed records response format:', responseData);
  return [];
}

export async function createFeedRecord(pondId: string, data: any) {
  const res = await api.post(`/ponds/${pondId}/feed-records`, data);
  return res.data;
}

export async function updateFeedRecord(recordId: string, data: any) {
  const res = await api.patch(`/ponds/feed-records/${recordId}`, data);
  return res.data;
}

export async function getSamplingRecords(pondId: string) {
  const res = await api.get(`/ponds/${pondId}/sampling-records`);
  return res.data;
}

export async function createSamplingRecord(pondId: string, data: any) {
  const res = await api.post(`/ponds/${pondId}/sampling-records`, data);
  return res.data;
}

export async function updateSamplingRecord(recordId: string, data: any) {
  console.log('updateSamplingRecord - recordId:', recordId);
  console.log('updateSamplingRecord - data:', data);
  const res = await api.patch(`/ponds/sampling-records/${recordId}`, data);
  return res.data;
}

export async function getWaterQualityRecords(pondId: string) {
  const res = await api.get(`/ponds/${pondId}/water-quality-records`);
  return res.data;
}

export async function createWaterQualityRecord(pondId: string, data: any) {
  const res = await api.post(`/ponds/${pondId}/water-quality-records`, data);
  return res.data;
}

export async function updateWaterQualityRecord(recordId: string, data: any) {
  const res = await api.patch(`/ponds/water-quality-records/${recordId}`, data);
  return res.data;
}

export async function getTreatmentRecords(pondId: string) {
  const res = await api.get(`/ponds/${pondId}/treatment-records`);
  return res.data;
}

export async function createTreatmentRecord(pondId: string, data: any) {
  const res = await api.post(`/ponds/${pondId}/treatment-records`, data);
  return res.data;
}

export async function updateTreatmentRecord(recordId: string, data: any) {
  const res = await api.patch(`/ponds/treatment-records/${recordId}`, data);
  return res.data;
}

export async function getMortalityRecords(pondId: string) {
  const res = await api.get(`/ponds/${pondId}/mortality-records`);
  return res.data;
}

export async function createMortalityRecord(pondId: string, data: any) {
  const res = await api.post(`/ponds/${pondId}/mortality-records`, data);
  return res.data;
}

export async function updateMortalityRecord(recordId: string, data: any) {
  const res = await api.patch(`/ponds/mortality-records/${recordId}`, data);
  return res.data;
}

export async function getDiseaseTests(pondId: string) {
  const res = await api.get(`/ponds/${pondId}/disease-test-records`);
  return res.data;
}

export async function createDiseaseTest(pondId: string, data: any) {
  const res = await api.post(`/ponds/${pondId}/disease-test-records`, data);
  return res.data;
}

export async function updateDiseaseTest(testId: string, data: any) {
  const res = await api.patch(`/ponds/disease-test-records/${testId}`, data);
  return res.data;
}

export async function getFeedingTrayChecks(pondId: string) {
  const res = await api.get(`/ponds/${pondId}/feeding-tray-checks`);
  return res.data;
}

export async function createFeedingTrayCheck(pondId: string, data: any) {
  const res = await api.post(`/ponds/${pondId}/feeding-tray-checks`, data);
  return res.data;
}

export async function updateFeedingTrayCheck(checkId: string, data: any) {
  const res = await api.patch(`/ponds/feeding-tray-checks/${checkId}`, data);
  return res.data;
}

export async function uploadFeedingTrayImages(checkId: string, files: File[]): Promise<string[]> {
  const formData = new FormData();
  files.forEach((file, index) => {
    formData.append('images', file);
  });

  const res = await api.post(`/ponds/feeding-tray-checks/${checkId}/upload-images`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  // Handle the wrapped response from TransformInterceptor
  const responseData = res.data;
  if (responseData && responseData.data) {
    return responseData.data;
  }
  
  // Fallback: if response is not wrapped, return as is
  return responseData;
}

export async function removeFeedingTrayImage(checkId: string, imageUrl: string): Promise<void> {
  const res = await api.delete(`/ponds/feeding-tray-checks/${checkId}/images/${encodeURIComponent(imageUrl)}`);
  
  // Handle the wrapped response from TransformInterceptor
  const responseData = res.data;
  if (responseData && responseData.data !== undefined) {
    return responseData.data;
  }
  
  // Fallback: if response is not wrapped, return as is
  return responseData;
}

export async function getImageSignedUrl(s3Key: string, pondId: string): Promise<string> {
  const response = await api.get(`/ponds/${pondId}/images/${encodeURIComponent(s3Key)}`);
  return response.data.signedUrl;
}

export async function getCurrentCycle(pondId: string) {
  const res = await api.get(`/ponds/${pondId}/cycles/current`);
  return res.data;
}

export interface HarvestRecord {
  id: string;
  harvestDate: string;
  count: number;
  totalWeight: number;
  survivalNumber?: number;
  harvestType: 'partial' | 'final';
  notes?: string;
  pondId: string;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateHarvestRecordDto {
  harvestDate: string;
  count: number;
  totalWeight: number;
  harvestType: 'partial' | 'final';
  notes?: string;
}

export interface UpdateHarvestRecordDto {
  harvestDate?: string;
  count?: number;
  totalWeight?: number;
  harvestType?: 'partial' | 'final';
  notes?: string;
}

export async function getHarvestRecords(pondId: string) {
  const res = await api.get(`/ponds/${pondId}/harvest-records`);
  return res.data;
}

export async function createHarvestRecord(pondId: string, data: CreateHarvestRecordDto) {
  const res = await api.post(`/ponds/${pondId}/harvest-records`, data);
  return res.data;
}

export async function updateHarvestRecord(recordId: string, data: UpdateHarvestRecordDto) {
  const res = await api.patch(`/ponds/harvest-records/${recordId}`, data);
  return res.data;
}

// New function to get performance metrics for all ponds
export async function getPondsWithPerformance(): Promise<Array<Pond & { performanceMetrics?: any }>> {
  try {
    const ponds = await fetchPonds();
    const pondsWithPerformance = await Promise.all(
      ponds.map(async (pond) => {
        try {
          const performanceResponse = await api.get(`/ponds/${pond.id}/performance`);
          const performanceData = performanceResponse.data;
          
          // Handle wrapped response from TransformInterceptor
          const metrics = performanceData?.data || performanceData;
          
          return {
            ...pond,
            performanceMetrics: metrics
          };
        } catch (error) {
          console.warn(`Could not fetch performance metrics for pond ${pond.id}:`, error);
          return {
            ...pond,
            performanceMetrics: null
          };
        }
      })
    );
    
    return pondsWithPerformance;
  } catch (error) {
    console.error('Error fetching ponds with performance:', error);
    throw error;
  }
}

// Feed Tray Analysis API
export interface FeedTrayAnalysisResult {
  weightEstimation: {
    totalWeight: number;
    averageWeight: number;
    count: number;
    confidence: number;
    shrimpLength?: number;
    shrimpLengthUnit?: string;
    individualWeight?: number;
  };
  diseaseDetection: {
    diseases: Array<{
      name: string;
      confidence: number;
      severity: 'low' | 'medium' | 'high';
    }>;
    overallHealth: 'healthy' | 'suspicious' | 'diseased';
  };
  imageQuality: {
    brightness: number;
    contrast: number;
    blur: number;
    recommendations: string[];
  };
  recommendations: string[];
}

export async function analyzeFeedTrayImage(file: File): Promise<FeedTrayAnalysisResult> {
  try {
    const formData = new FormData();
    formData.append('image', file);

    const response = await api.post<BackendResponse<FeedTrayAnalysisResult>>('/feed-tray-analysis/analyze', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: return the response directly if it's not wrapped
    if (responseData && (responseData as any).weightEstimation) {
      return responseData as unknown as FeedTrayAnalysisResult;
    }
    
    throw new Error('Invalid response format from analysis endpoint');
  } catch (error) {
    console.error('Error analyzing feed tray image:', error);
    throw error;
  }
}
